import React from 'react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import DataTable from 'react-data-table-component';
import HeaderBanner from '../../../common/HeaderBanner';
import { formatDate } from '../../../../utils/common';
import { Download, Expand, FileSpreadsheet, Printer, Shrink } from 'lucide-react';
import FilterSection from '../../../common/Filter';
import Select from 'react-select';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import NewDropdown from '../../../common/NewDropDown';
import { exportBiddersReport, getBiddersReport } from './service/reportService';
import { getAuctionList } from '../Auction/services/auctionService';
import { getGroupList } from '../Group/services/groupServices';

class ReportNew extends React.Component {
  constructor() {
    super();
    this.state = {
      search: {
        group: null,
        auction: null,
      },
      bidders: [],
      groups: [],
      auctions: [],
      isLoading: false,
      limit: 15,
      page: 1,
      total: 0,
      exportLoading: false,
      auctionLoading: false,
      groupLoading: false,
      hideSidebar: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    const groupParam = params.get('group');
    const auctionParam = params.get('auction');

    const search = {
      group: groupParam ? parseInt(groupParam) : null,
      auction: auctionParam ? parseInt(auctionParam) : null,
    };

    this.setState({ search, page, limit }, () => {
      this.fetchGroupList();
      if (search.group) {
        this.fetchAuctionList();
        this.fetchBiddersReport();
      }
    });
  }

  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchBiddersReport() {
    this.setState({ isLoading: true });
    const { limit, page } = this.state;
    const { group, auction } = this.state.search;

    const q = { page, limit };
    if (group) q.group = group;
    if (auction) q.auction = auction;

    this.updateUrlQuery(q);

    const response = await getBiddersReport(q);
    if (response.success) {
      this.setState({
        bidders: response.data,
        total: response.meta.total,
      });
    }
    this.setState({ isLoading: false });
  }

  async fetchGroupList() {
    this.setState({ groupLoading: true });
    const response = await getGroupList();
    if (response.success) {
      const groups = response.data.map((item) => ({
        value: item.id, // number from API
        label: item.group_name,
      }));
      this.setState({ groups, groupLoading: false });
    } else {
      this.setState({ groups: [], groupLoading: false });
    }
  }

  async fetchAuctionList() {
    this.setState({ auctionLoading: true });
    const { group } = this.state.search;
    const q = group ? { groupId: group } : {};
    const response = await getAuctionList(q);
    if (response.success) {
      const auctions = response.data.map((item) => ({
        value: item.id,
        label: item.title,
      }));
      this.setState({ auctions, auctionLoading: false });
    } else {
      this.setState({ auctionLoading: false });
    }
  }

  handleDownload = async (fileType) => {
    const { group, auction } = this.state.search;
    if (!group) return;

    this.setState({ exportLoading: true });
    try {
      const params = { fileType, group };
      if (auction) params.auction = auction;

      await exportBiddersReport({
        params,
        fileType,
        fileName: 'Bidders_Report',
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  getColumns = () => {
    const { t } = i18n;

    return [
      {
        name: t('bidder_id'),
        selector: (row) => row.id,
        width: '100px',
        wrap: true,
      },
      {
        name: t('name'),
        selector: (row) => row.user?.name,
        cell: (row) => row.user?.name || '-',
        minWidth: '140px',
        wrap: true,
      },
      {
        name: t('username'),
        selector: (row) => row.user?.username,
        cell: (row) => row.user?.username || '-',
        width: '140px',
        wrap: true,
      },
      {
        name: t('group'),
        selector: (row) => row.auction?.group_relation?.group_name,
        cell: (row) => row.auction?.group_relation?.group_name || '-',
        minWidth: '160px',
        wrap: true,
      },
      {
        name: t('auction'),
        selector: (row) => row.auction?.title,
        cell: (row) => row.auction?.title || '-',
        minWidth: '160px',
        wrap: true,
      },
      {
        name: t('latest_bid_amount'),
        selector: (row) => row.bid_amount,
        cell: (row) => row.bid_amount ?? '-',
        minWidth: '140px',
        right: true,
      },
      {
        name: t('bid_date'),
        selector: (row) => row.created_at,
        cell: (row) => formatDate(row.created_at) || '-',
        minWidth: '160px',
        wrap: true,
      },
      {
        name: t('start_date'),
        selector: (row) => row.auction?.start_date,
        cell: (row) => (
          <span className="highlight-table-value">
            {formatDate(row.auction?.start_date) || '-'}
          </span>
        ),
        width: '130px',
        wrap: true,
      },
      {
        name: t('end_date'),
        selector: (row) => row.auction?.end_date,
        cell: (row) => (
          <span className="highlight-table-value">{formatDate(row.auction?.end_date) || '-'}</span>
        ),
        width: '130px',
        wrap: true,
      },
    ];
  };

  //  Group onChange — store as integer, clear auctions, load auction list
  handleGroupChange = (selectedOption) => {
    const groupValue = selectedOption ? parseInt(selectedOption.value) : null;
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          group: groupValue,
          auction: null,
        },
        auctions: [],
        bidders: [],
        total: 0,
      }),
      () => {
        if (groupValue) {
          this.fetchAuctionList();
        }
      }
    );
  };

  //  Auction onChange — store as integer
  handleAuctionChange = (selectedOption) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        auction: selectedOption ? parseInt(selectedOption.value) : null,
      },
    }));
  };

  //  Guard: only search if group is selected
  handleSearch = () => {
    const { group } = this.state.search;
    if (!group) {
      return;
    }
    this.setState({ page: 1 }, () => {
      this.fetchBiddersReport();
    });
  };

  //  Clear: reset state, do NOT call fetchBiddersReport (no group selected)
  handleClear = () => {
    this.setState(
      {
        search: { group: null, auction: null },
        page: 1,
        bidders: [],
        total: 0,
        auctions: [],
      },
      () => {
        this.fetchGroupList();
      }
    );
  };

  onChangePage = (page) => {
    this.setState({ page }, () => {
      this.fetchBiddersReport();
    });
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit, page: 1 }, () => {
      this.fetchBiddersReport();
    });
  };

  render() {
    const {
      bidders,
      isLoading,
      groups,
      auctions,
      exportLoading,
      groupLoading,
      auctionLoading,
      hideSidebar,
    } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('report')} subtitle={i18n.t('report_subtitle')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? <Shrink strokeWidth={1} /> : <Expand strokeWidth={1} />}
                      </span>
                    </div>
                    {bidders.length > 0 && (
                      <div className="header-action">
                        <NewDropdown
                          label={i18n.t('export')}
                          loading={exportLoading}
                          icon={Download}
                          items={[
                            {
                              label: i18n.t('excel'),
                              icon: FileSpreadsheet,
                              onClick: () => this.handleDownload('excel'),
                            },
                            {
                              label: i18n.t('pdf'),
                              icon: Printer,
                              onClick: () => this.handleDownload('pdf'),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>

                  <div className="card-body">
                    <FilterSection
                      loading={isLoading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      {/* Group Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={groups}
                          //  compare number === number (both are integers now)
                          value={groups.find((c) => c.value === this.state.search.group) || null}
                          onChange={this.handleGroupChange}
                          name="group"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={
                            groupLoading ? `${i18n.t('loading')}...` : i18n.t('select_group')
                          }
                          isLoading={groupLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('group_name')}</label>
                      </div>

                      {/* Auction Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={auctions}
                          //  compare number === number
                          value={
                            auctions.find((c) => c.value === this.state.search.auction) || null
                          }
                          onChange={this.handleAuctionChange}
                          name="auction"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={
                            auctionLoading ? `${i18n.t('loading')}...` : i18n.t('select_auction')
                          }
                          isLoading={auctionLoading}
                          isDisabled={!this.state.search.group || auctionLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('auction_name')}</label>
                      </div>
                    </FilterSection>

                    <div className="Data-Listing">
                      {isLoading ? (
                        <TableSkeleton columns={9} rows={10} />
                      ) : bidders.length ? (
                        <DataTable
                          responsive={true}
                          columns={this.getColumns()}
                          data={bidders}
                          customStyles={tableStyles}
                          highlightOnHover
                          pointerOnHover
                          pagination
                          paginationServer
                          paginationTotalRows={this.state.total}
                          paginationPerPage={this.state.limit}
                          paginationDefaultPage={this.state.page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangePage={this.onChangePage}
                          onChangeRowsPerPage={this.onChangeRowsPerPage}
                        />
                      ) : (
                        <NoData
                          message={i18n.t('no_bidders_available')}
                          //   actionText={i18n.t('refresh')}
                          //   onAction={this.fetchBiddersReport.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default ReportNew;
